<script setup lang="ts">
/**
 * Phones and tablets (below lg): the four most-used destinations the user may
 * open (nav.ts MOBILE_TABS) plus "More", which opens the full navigation.
 * 64px high (+1px border) plus the home-indicator inset, published as
 * --tabbar-offset (main.css) so page bars sit above it; targets are 44px+.
 */
import { Menu } from "@lucide/vue";
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useSession } from "@/composables/useSession";
import { cn } from "@/lib/utils";
import { activeItem, mobileTabs, NAV } from "./nav";
import { usePendingApprovals } from "./usePendingApprovals";

defineProps<{ moreOpen?: boolean }>();
defineEmits<{ more: [] }>();

const route = useRoute();
const { allows } = useSession();
const pendingCount = usePendingApprovals();

const tabs = computed(() => mobileTabs((i) => allows(i.requires)));
const active = computed(() =>
  activeItem(
    NAV.flatMap((s) => s.items).filter((i) => allows(i.requires)),
    route.path,
  ),
);
/** The current page is not one of the tabs: "More" is where it lives. */
const moreActive = computed(() => !active.value || !tabs.value.includes(active.value));

const itemClass = (selected: boolean) =>
  cn(
    'group flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-lg pt-1.5 pb-1 text-[11px] leading-none font-medium transition-colors duration-100 focus-visible:outline-offset-[-2px]',
    selected ? 'text-primary' : 'text-muted-foreground hover:text-foreground active:text-foreground',
  );
const pillClass = (selected: boolean) =>
  cn(
    'relative grid h-7 w-12 place-items-center rounded-full transition-colors duration-150',
    selected ? 'bg-state-selected' : 'group-hover:bg-state-hover group-active:bg-state-press',
  );
</script>

<template>
  <nav
    aria-label="Quick navigation"
    class="fixed inset-x-0 bottom-0 z-40 border-t border-border/80 bg-background/85 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl backdrop-saturate-150 supports-[backdrop-filter]:bg-background/70 lg:hidden"
  >
    <ul role="list" class="mx-auto flex h-16 max-w-xl items-stretch gap-1 px-2">
      <li v-for="tab in tabs" :key="tab.to" class="flex min-w-0 flex-1">
        <RouterLink :to="tab.to" :aria-current="active === tab ? 'page' : undefined" :class="itemClass(active === tab)">
          <span :class="pillClass(active === tab)">
            <component :is="tab.icon" class="size-5" aria-hidden="true" />
            <span
              v-if="tab.badge === 'approvals' && pendingCount > 0"
              class="absolute -top-1 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-[10px] font-semibold text-black/80 tabular-nums ring-2 ring-background"
              :aria-label="`${pendingCount} waiting`"
            >
              {{ pendingCount > 99 ? "99+" : pendingCount }}
            </span>
          </span>
          <span class="max-w-full truncate px-0.5">{{ tab.shortLabel ?? tab.label }}</span>
        </RouterLink>
      </li>
      <li class="flex min-w-0 flex-1">
        <button
          type="button"
          :class="itemClass(moreActive || Boolean(moreOpen))"
          :aria-expanded="Boolean(moreOpen)"
          aria-haspopup="dialog"
          @click="$emit('more')"
        >
          <span :class="pillClass(moreActive || Boolean(moreOpen))">
            <Menu class="size-5" aria-hidden="true" />
          </span>
          <span>More</span>
        </button>
      </li>
    </ul>
  </nav>
</template>
